(() => {
  const TAG = 'kleone-bundle-upsell';
  if (customElements.get(TAG)) return;

  class KleoneBundleUpsell extends HTMLElement {
    connectedCallback() {
      this.abort?.abort();
      this.stopCountdown();
      this.abort = new AbortController();
      const signal = this.abort.signal;
      this.dialog = this.querySelector('dialog');
      this.countdown = this.querySelector('[data-kbu-countdown]');
      this.status = this.querySelector('[data-kbu-status]');
      this.acceptButton = this.querySelector('[data-kbu-accept]');
      this.acceptLabel = this.querySelector('[data-kbu-accept-label]');
      this.defaultLabel = this.acceptLabel.textContent;
      this.declineLabel = this.querySelector('.kbu-decline').textContent;

      // Listeners are inert in Control: no observers, requests, form edits or storage writes.
      document.addEventListener('click', (event) => this.intercept(event), { capture: true, signal });
      document.addEventListener('submit', (event) => this.intercept(event), { capture: true, signal });
      document.addEventListener('cart:update', (event) => this.onCart(event), { capture: true, signal });
      document.addEventListener('cart:error', (event) => this.onCart(event, true), { capture: true, signal });
      this.acceptButton.addEventListener('click', () => this.choose(true), { signal });
      this.querySelectorAll('[data-kbu-decline]').forEach((button) => {
        button.addEventListener('click', () => this.choose(false), { signal });
      });
      this.dialog.addEventListener('cancel', (event) => {
        event.preventDefault();
        this.choose(false);
      }, { signal });
      this.dialog.addEventListener('click', (event) => {
        if (event.target !== this.dialog) return;
        const rect = this.dialog.getBoundingClientRect();
        if (event.clientX < rect.left || event.clientX > rect.right ||
          event.clientY < rect.top || event.clientY > rect.bottom) this.choose(false);
      }, { signal });
      this.dialog.addEventListener('close', () => {
        this.stopCountdown();
        this.restoreScroll();
      }, { signal });
      document.addEventListener('visibilitychange', () => {
        if (this.dialog.open && !document.hidden) this.updateCountdown();
      }, { signal });
    }

    disconnectedCallback() {
      this.abort?.abort();
      this.stopCountdown();
      clearTimeout(this.submitTimeout);
      if (this.dialog?.open) this.dialog.close();
      this.restoreScroll();
    }

    updateCountdown(now = new Date()) {
      if (!this.countdown) return;
      // Recalculate from the wall clock so sleep, delayed ticks and DST cannot accumulate drift.
      const midnight = new Date(now.getFullYear(), now.getMonth(), now.getDate() + 1);
      const seconds = Math.ceil((midnight.getTime() - now.getTime()) / 1000);
      const parts = [Math.floor(seconds / 3600), Math.floor(seconds % 3600 / 60), seconds % 60];
      const digits = this.countdown.querySelectorAll('[data-kbu-time]');
      const formatted = parts.map((part) => String(part).padStart(2, '0'));
      if (digits.length === 3) {
        digits.forEach((digit, index) => { digit.textContent = formatted[index]; });
      } else {
        // Preserve the timer if an older cached section is paired with this asset.
        this.countdown.textContent = formatted.join(':');
      }
      this.countdown.setAttribute('aria-label', `${parts[0]} hours, ${parts[1]} minutes, ${parts[2]} seconds until your local midnight`);
    }

    startCountdown() {
      this.stopCountdown();
      this.updateCountdown();
      this.countdownInterval = setInterval(() => this.updateCountdown(), 1000);
    }

    stopCountdown() {
      clearInterval(this.countdownInterval);
      this.countdownInterval = null;
    }

    active() {
      const path = location.pathname.replace(/\/$/, '');
      const root = (window.Shopify?.routes?.root || '/').replace(/\/$/, '');
      return path === `${root}/products/${this.dataset.productHandle}` &&
        getComputedStyle(this).display !== 'none';
    }

    readJSON(element, attribute) {
      try { return JSON.parse(element?.getAttribute(attribute) || 'null'); }
      catch { return null; }
    }

    money(cents, format) {
      const formats = {
        amount: [2, ',', '.'],
        amount_no_decimals: [0, ',', '.'],
        amount_with_comma_separator: [2, '.', ','],
        amount_no_decimals_with_comma_separator: [0, '.', ','],
        amount_with_apostrophe_separator: [2, "'", '.'],
        amount_with_space_separator: [2, ' ', ','],
        amount_no_decimals_with_space_separator: [0, ' ', ','],
        amount_with_period_and_space_separator: [2, ' ', '.'],
      };
      const match = format?.match(/\{\{\s*(\w+)\s*\}\}/);
      if (!match || !formats[match[1]] || !Number.isFinite(cents)) return null;
      const [precision, thousands, decimal] = formats[match[1]];
      const parts = (cents / 100).toFixed(precision).split('.');
      parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
      const plain = new DOMParser().parseFromString(format, 'text/html').body.textContent;
      return plain.replace(match[0], parts.join(decimal));
    }

    sameMoney(a, b) {
      const normalize = (value) => value?.replace(/\s/g, '').trim();
      return !!a && !!b && normalize(a) === normalize(b);
    }

    readOffer(form) {
      const component = form.closest('product-form-component');
      if (!component || component.dataset.productId !== this.dataset.productId) return null;
      const block = component.querySelector('kaching-bundles-block');
      const deals = this.readJSON(block, 'deal-block');
      const product = this.readJSON(block, 'product');
      const config = this.readJSON(block, 'config');
      const variantId = form.querySelector('[name="id"]')?.value;
      const variant = product?.variants?.find((item) => String(item.id) === variantId);
      if (!deals || !config || !variant?.availableForSale || product.requiresSellingPlan ||
        Number(product.id) !== Number(this.dataset.productId) ||
        form.querySelector('[name="selling_plan"]')?.value) return null;

      // Match the actual entitlement rather than a mutable offer title or hardcoded deal ID.
      const single = deals.dealBars?.find((deal) =>
        deal.dealBarType === 'quantity-break' && deal.quantity === 1 && deal.discountType === 'default');
      const upgrade = deals.dealBars?.find((deal) =>
        deal.dealBarType === 'bxgy' && deal.buyQuantity === 3 && deal.getQuantity === 2 &&
        deal.buyDiscountType === 'default' && deal.getDiscountType === 'percentage' && deal.getDiscountValue === 100);
      const usable = (deal) => deal && !deal.sellingPlanEnabled && !deal.showAsSoldOutEnabled &&
        !deal.hideBehindScratchOff && !deal.productPersonalisation && !(deal.upsells?.length);
      if (!usable(single) || !usable(upgrade)) return null;

      const findBar = (id) => [...block.querySelectorAll('[data-deal-bar-id]')]
        .find((bar) => bar.dataset.dealBarId === id);
      const singleBar = findBar(single.id);
      const upgradeBar = findBar(upgrade.id);
      const radio = upgradeBar?.querySelector('input[type="radio"]');
      const button = form.querySelector('button[type="submit"]');
      if (!singleBar || !upgradeBar || !radio || radio.disabled || !button || button.disabled ||
        button.getAttribute('aria-busy') === 'true') return null;

      const singlePrice = singleBar.querySelector('.kaching-bundles__bar-price')?.textContent.trim();
      const total = upgradeBar.querySelector('.kaching-bundles__bar-price')?.textContent.trim();
      const unit = Number(variant.price);
      if (!(unit > 0) || !this.sameMoney(singlePrice, this.money(unit, config.moneyFormat)) ||
        !this.sameMoney(total, this.money(unit * 3, config.moneyFormat))) return null;

      return {
        form, component, block, single, upgrade, singleBar, upgradeBar, button, variantId,
        total, extra: this.money(unit * 2, config.moneyFormat),
        quantity: Number(form.querySelector('[name="quantity"]')?.value || 1),
        selected: block.querySelector('.kaching-bundles__bar--selected')?.dataset.dealBarId,
      };
    }

    intercept(event) {
      if (this.allowSubmit || !this.active()) return;
      const target = event.target instanceof Element ? event.target : null;
      const button = event.type === 'click' ? target?.closest('button[type="submit"], input[type="submit"]') : null;
      const form = event.type === 'submit' ? target : button?.form;
      if (!(form instanceof HTMLFormElement) || form.closest('product-form-component')?.dataset.productId !== this.dataset.productId) return;
      if (this.pending || this.dialog.open) {
        event.preventDefault();
        event.stopImmediatePropagation();
        return;
      }
      const offer = this.readOffer(form);
      if (!offer || offer.quantity !== 1 || offer.selected !== offer.single.id || !form.checkValidity()) return;
      if (document.querySelector('dialog[open]')) return;

      this.offer = offer;
      this.trigger = document.activeElement;
      // Each eligible purchase is a new decision, including after a decline or a page revisit.
      // Clear error UI from an earlier attempt without ever retrying an uncertain cart request.
      this.failed = false;
      this.acceptButton.hidden = false;
      this.querySelector('[data-kbu-check-cart]').hidden = true;
      this.querySelector('.kbu-decline').textContent = this.declineLabel;
      this.querySelector('[data-kbu-total]').textContent = offer.total;
      this.querySelector('[data-kbu-extra]').textContent = offer.extra.replace(/[.,]00(?=\D*$)/, '');
      this.status.textContent = '';
      try { this.dialog.showModal(); }
      catch { return; } // Native purchase continues if dialog support is unavailable.
      this.startCountdown();
      event.preventDefault();
      event.stopImmediatePropagation();
      this.previousOverflow = document.documentElement.style.overflow;
      document.documentElement.style.overflow = 'hidden';
      this.querySelector('[data-kbu-decline]').focus({ preventScroll: true });
      this.track('shown');
    }

    setBusy(busy, upgrade = true) {
      this.toggleAttribute('data-busy', busy);
      this.toggleAttribute('data-upgrading', busy && upgrade);
      this.dialog.setAttribute('aria-busy', String(busy));
      this.querySelectorAll('button').forEach((button) => { button.disabled = busy; });
      this.acceptLabel.textContent = busy && upgrade ? 'Adding to your cart…' : this.defaultLabel;
      this.querySelector('.kbu-decline').textContent = busy && !upgrade
        ? 'Adding just 1 antenna to your cart…' : this.declineLabel;
    }

    selectedAndSynced(offer, deal, quantity) {
      const data = new FormData(offer.form);
      let properties;
      try { properties = JSON.parse(data.get('properties[__kaching_bundles]') || 'null'); }
      catch { return false; }
      return offer.block.querySelector('.kaching-bundles__bar--selected')?.dataset.dealBarId === deal.id &&
        Number(data.get('quantity') || 1) === quantity && properties?.bid === deal.id &&
        String(data.get('id')) === offer.variantId;
    }

    async selectAndWait(offer, deal, quantity) {
      const bar = [...offer.block.querySelectorAll('[data-deal-bar-id]')]
        .find((element) => element.dataset.dealBarId === deal.id);
      const radio = bar?.querySelector('input[type="radio"]');
      if (!radio || radio.disabled) return false;
      radio.click();
      // Kaching updates its native form asynchronously after selecting the real tier.
      for (let attempt = 0; attempt < 30; attempt += 1) {
        if (!this.isConnected || !offer.form.isConnected) return false;
        if (this.selectedAndSynced(offer, deal, quantity)) return true;
        await new Promise((resolve) => setTimeout(resolve, 50));
      }
      return false;
    }

    async choose(upgrade) {
      if (this.pending || !this.dialog.open) return;
      if (this.failed) {
        this.dialog.close();
        this.trigger?.focus?.({ preventScroll: true });
        return;
      }
      this.pending = true;
      this.setBusy(true, upgrade);
      const original = this.offer;
      const fresh = original.form.isConnected && this.readOffer(original.form);
      if (!fresh || fresh.variantId !== original.variantId || fresh.total !== original.total ||
        fresh.extra !== original.extra || !this.active()) {
        this.fail('Your selection changed. Close this window and choose your pack again.', false);
        return;
      }

      const deal = upgrade ? fresh.upgrade : fresh.single;
      const quantity = upgrade ? 5 : 1;
      const synced = await this.selectAndWait(fresh, deal, quantity);
      if (!synced || !fresh.form.reportValidity()) {
        await this.selectAndWait(fresh, fresh.single, 1);
        this.fail('We couldn’t select this offer. Close this window and choose your pack again.', false);
        return;
      }
      // Re-read prices, availability and entitlement after Kaching's async update.
      const checked = this.readOffer(fresh.form);
      if (!checked || checked.total !== original.total || checked.extra !== original.extra ||
        !this.selectedAndSynced(checked, deal, quantity)) {
        await this.selectAndWait(fresh, fresh.single, 1);
        this.fail('The offer has changed. Close this window and choose your pack again.', false);
        return;
      }
      this.offer = checked;
      this.track(upgrade ? 'accepted' : 'declined');
      this.submitted = true;
      this.allowSubmit = true;
      try {
        checked.button.click();
      } finally {
        this.allowSubmit = false;
      }
      this.track('submitted', { quantity });
      this.submitTimeout = setTimeout(() => {
        if (this.pending) this.fail('We couldn’t confirm the update. Check your cart before adding again.', true);
      }, 15000);
    }

    onCart(event, error = false) {
      if (!this.pending || !this.submitted) return;
      const source = event.target instanceof Element ? event.target : null;
      if (source?.closest('product-form-component') !== this.offer.component) return;
      clearTimeout(this.submitTimeout);
      if (error || event.detail?.data?.didError) {
        this.fail('We couldn’t confirm the update. Check your cart before adding again.', true);
        return;
      }
      this.pending = false;
      this.submitted = false;
      this.setBusy(false);
      this.dialog.close(); // Close before the existing cart drawer receives the same event.
      this.track('added');
    }

    fail(message, uncertain) {
      clearTimeout(this.submitTimeout);
      this.pending = false;
      this.submitted = false;
      this.failed = true;
      this.setBusy(false);
      this.status.textContent = message;
      this.acceptButton.hidden = true;
      this.querySelector('[data-kbu-check-cart]').hidden = !uncertain;
      this.querySelector('.kbu-decline').textContent = 'Back to my selection';
      this.track('error', { uncertain });
    }

    restoreScroll() {
      if (this.previousOverflow === undefined) return;
      if (document.documentElement.style.overflow === 'hidden') {
        document.documentElement.style.overflow = this.previousOverflow;
      }
      this.previousOverflow = undefined;
    }

    track(action, data = {}) {
      this.dispatchEvent(new CustomEvent('maison:antenna-upsell', {
        bubbles: true,
        detail: { testId: this.getAttribute('maison-test-id'), action, productId: this.dataset.productId, ...data },
      }));
    }
  }

  customElements.define(TAG, KleoneBundleUpsell);
})();
